import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import qs from 'qs'
import Divider from 'LuxKit/Divider'
import offerPageURL from 'lib/offer/offerPageURL'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import moment from 'moment/moment'
import CruiseSoldoutDepartureCard from 'components/OfferPage/Cruises/CruiseSoldoutDepartureCard'
import CruiseSoldoutStillInterested from 'components/OfferPage/Cruises/CruiseSoldoutStillInterested'
import CruiseModalPricesItem from 'components/Cruises/CruiseTiles/Common/CruiseModalPrices/CruiseModalPricesItem'
import { sortBy, groupBy } from 'lib/array/arrayUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Modal from 'LuxKit/Modal/Modal'
import { isCruisePromotionPrice } from 'lib/cruises/cruiseUtils'

interface Props {
  offerTitle: string
  offer?: App.CruiseOffer | App.Offer | App.OfferSummary
  departures?: Record<string, Array<App.CruiseDepartureSummary>>
  startDateFilter?: moment.Moment | string
  endDateFilter?: moment.Moment | string
  monthsFilter?: string
}

function getDeparturePricePerPerson(departure: App.CruiseDepartureSummary): number {
  return departure.lowestPriceTotal
}

function getGroupedDeparturesByYear(
  departureList: Array<App.CruiseDepartureSummary>,
  startDateFilter?: moment.Moment | string,
  endDateFilter?: moment.Moment | string,
  monthsFilter?: string,
) {
  const departureListSorted = sortBy(departureList, ({ departureDate }) => new Date(departureDate).getTime(), 'asc')
  const groupedDataByYear = groupBy(departureListSorted, ({ departureDate }) => new Date(departureDate).getFullYear())
  const cheapestDeparturePrice = sortBy(departureList, getDeparturePricePerPerson, 'asc')[0]
  const months = monthsFilter?.split(',') || []

  return Array.from(groupedDataByYear.entries()).map(([year, departures]) => {
    const departuresData = departures.map(departure => {
      const isCheapest = !!cheapestDeparturePrice &&
        getDeparturePricePerPerson(departure) === getDeparturePricePerPerson(cheapestDeparturePrice)
      let hasSelectedDates = false

      if (startDateFilter && endDateFilter) {
        hasSelectedDates = hasSelectedDates || (
          moment(departure.departureDate).isSameOrAfter(startDateFilter) &&
          moment(departure.departureDate).isSameOrBefore(endDateFilter)
        )
      }

      if (months.length) {
        hasSelectedDates = hasSelectedDates || months.includes(moment(departure.departureDate).format('YYYY-MM'))
      }

      return { ...departure, isCheapest, hasSelectedDates }
    })

    // set isCheapest to false when all offers has the same price
    const everyDeparturesSamePrice = departuresData.every(departure =>
      !!cheapestDeparturePrice && getDeparturePricePerPerson(departure) === getDeparturePricePerPerson(cheapestDeparturePrice))
    if (everyDeparturesSamePrice) {
      departuresData.forEach((departure) => {
        departure.isCheapest = false
      })
    }

    return {
      year,
      departures: departuresData,
    }
  })
}

function CruiseModalPrices({
  offerTitle,
  offer,
  departures,
  startDateFilter,
  endDateFilter,
  monthsFilter,
}: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { resolve } = useModalElementContext()

  const onClickModal = useCallback((departureDate: string) => {
    resolve()

    const url = offerPageURL(offer)
    const month = moment(departureDate).format('YYYY-MM')
    dispatch(pushWithRegion(url, qs.stringify({
      departureMonths: month,
      scrollToPackages: true,
    })))
  }, [resolve, dispatch, offer])

  const departureList = useMemo(() => Object.values(departures || {}).flat(), [departures])

  const departuresByYear = useMemo(
    () => getGroupedDeparturesByYear(departureList, startDateFilter, endDateFilter, monthsFilter),
    [departureList, startDateFilter, endDateFilter, monthsFilter],
  )

  const hasValidDepartures = departureList.length > 0

  const modalTitle = intl.formatMessage(
    {
      id: 'p7Hz4mon5iBIlVJnN1UE',
      defaultMessage: 'Dates & Pricing for {offerTitle}',
      description: 'Modal title for cruise dates and pricing in the cruise section',
    },
    { offerTitle },
  )

  return <Modal
    height="auto"
    title={modalTitle}
  >
    <Group direction="vertical" gap={32} data-testid="cruise-modal-prices-offer">
      {departuresByYear.map(({ year, departures }) => <Group direction="vertical" gap={8} key={year}>
        <Heading variant="heading6">{year}</Heading>

        <div>
          {departures.map((departure, index) => <div key={index}>
            <CruiseModalPricesItem
              departureDate={departure.departureDate}
              arrivalDate={departure.arrivalDate}
              price={isCruisePromotionPrice(departure) ? departure.promotionalPrice : departure.lowestPriceTotal}
              promotion={isCruisePromotionPrice(departure) ? departure.promotion : undefined}
              isCheapest={departure.isCheapest}
              hasSelectedDates={departure.hasSelectedDates}
              onClick={onClickModal}
            />
            {index !== departures.length - 1 && <Divider kind="secondary" />}
          </div>)}
        </div>
      </Group>)}

      {!hasValidDepartures && <>
        <CruiseSoldoutDepartureCard />
        <CruiseSoldoutStillInterested useLinkButton />
      </>}
    </Group>
  </Modal>
}

export default CruiseModalPrices
